import React from "react";
import { observable, action } from "mobx";
import { addMonths } from "date-fns";
import { Formatter } from "libs";
import * as Api from "api";
import * as ChurnSegment from "api/agencies/ChurnSegment";

interface Value {
  id?: number;
  twitterUserId?: string;
  twitterSegmentId?: string;
  name?: string;
  predictionRate?: number;
  startAt?: string;
  endAt?: string;
}

export class ChurnSegmentFormStore {
  @observable.ref
  public dialog = false;

  @observable.ref
  public errors = new Api.Error<ChurnSegment.CreateParams>();

  @observable
  public value: Value = {};

  @action.bound
  public modal(dialog: boolean, value?: Value) {
    this.dialog = dialog;
    this.errors.clear();
    this.value = value || {
      predictionRate: 70,
      startAt: Formatter.date(new Date()),
      endAt: Formatter.date(addMonths(new Date(), 6)),
    };
  }

  public async save(agencyId: number, appId: number) {
    this.errors.clear();

    const params = {
      twitterSegmentId: this.value.twitterSegmentId,
      name: this.value.name,
      deliveryRate: (100 - (this.value.predictionRate || 0) + 10) / 100,
      startAt: this.value.startAt,
      endAt: this.value.endAt,
    };

    const response = this.value.id
      ? await ChurnSegment.Api.update(agencyId, this.value.id, params)
      : await ChurnSegment.Api.create(agencyId, {
          appId,
          ...params,
        });

    if (response?.status === Api.Status.OK) {
      this.modal(false);
      return true;
    }

    this.errors.set(response?.data);

    return false;
  }
}

const Context = React.createContext<ChurnSegmentFormStore | null>(null);

export const ChurnSegmentFormStoreProvider: React.FC<{
  value?: ChurnSegmentFormStore;
}> = ({ children, value }) => {
  const store = React.useMemo(
    () => (value ? value : new ChurnSegmentFormStore()),
    [value]
  );
  return <Context.Provider value={store}>{children}</Context.Provider>;
};

export const useChurnSegmentFormStore = () => {
  const store = React.useContext(Context);
  if (!store) {
    throw new Error(
      "useChurnSegmentFormStore must be used within a ChurnSegmentFormStoreProvider."
    );
  }
  return store;
};
